import {ILayerView, IScheduleLayerView} from "@esWebAPI/es/model/Layer";
import {
    DataDrivenPropertyValueSpecification,
    ExpressionFilterSpecification,
    ExpressionSpecification
} from "@maplibre/maplibre-gl-style-spec";
import {ColorThematic} from "@esWebAPI/es/model/Thematic";
import TerritoryModel, {ITerritoryFeature} from "@esWebAPI/es/map/TerritoryModel";
import {NavUtils} from "@esWebAPI/es/util/NavUtils";
import {TerritoryPointStyleSettings} from "@esWebAPI/es/model/Models";
import {IRoutingModule} from "@esWebAPI/es/map/module/ScheduleModule";
import _ from "lodash";


export interface ColorThematicStyler {
    getThematic(layerView: ILayerView): Promise<ColorThematic|undefined> ;
    getServerExpression(layerView: ILayerView, colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<number>;
    getHexExpression(layerView: ILayerView, colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<string>;
    getOpacity(layerView: ILayerView): DataDrivenPropertyValueSpecification<number>;
    getFillHex(layerView: ILayerView, colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<string>;
    getFillOpacity(layerView: ILayerView): DataDrivenPropertyValueSpecification<number>;
    getStrokeHex(layerView: ILayerView, colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<string>;
    getStrokeOpacity(layerView: ILayerView): DataDrivenPropertyValueSpecification<number>;
    getFilterExpression(layerView: ILayerView, colorThematic?: ColorThematic): ExpressionFilterSpecification | undefined;
}

export class DefaultColorThematicStyler implements ColorThematicStyler {

    private getAttrValue(colorThematic: ColorThematic|undefined) {
        if(!colorThematic) {
            throw new Error("getAttrValue called without ColorThematic");
        }
        const colorAttr = colorThematic.attribute;
        if (colorThematic.$type === "DistinctColorThematic") {
            return ['get', colorAttr];
        }
        else if (colorThematic.$type === "RangeColorThematic") {
            return ['to-number', ['get', colorAttr]];
        }
        throw new Error("Unsupported color thematic type: " + JSON.stringify(colorThematic.$type));
    }

    public async getThematic(layerView: ILayerView): Promise<ColorThematic|undefined> {
        const overrideThematic = layerView.styleSettings.colorThematicSettings?.overrideThematic as (ColorThematic|undefined);
        if (overrideThematic) {
            return overrideThematic;
        } else if (layerView.styleSettings.colorThematicSettings?.enabled) {
            return es.api.thematic.getColorThematic(layerView.id, layerView.styleSettings.colorThematicSettings.attributeName, { noCache: true });
        }
    }

    private getColorExpression<T extends number | string>(layerView: ILayerView,
                                  isServer: boolean,
                                  simplify: boolean,
                                  colorThematic: ColorThematic|undefined,
                                  defaultStyleColor?: number): DataDrivenPropertyValueSpecification<T>  {

        let defaultServerColor: number =
            defaultStyleColor ||
            layerView.styleSettings.defaultStyle?.symbol?.color ||
            layerView.styleSettings.defaultStyle?.color ||
            layerView.styleSettings.defaultStyle?.symbolStyle?.color ||
            layerView.styleSettings.defaultStyle?.line?.color;

        //es.util.color._debug(defaultServerColor, "getColorExpression : " + layerView.id + ", " + layerView.$type + ", " + layerView.title);

        const getColor = (color: number) => {
            if (isServer) {
                return (simplify ? es.util.color.simplifyServer(color) : color) as T;
            }
            return es.util.color.serverToHex(color) as T;
        };

        if (!defaultServerColor){
            es.log.error("getStyleExpression could not determine a default color to use", layerView.styleSettings?.defaultStyle);
        }

        const defaultColor = getColor(defaultServerColor);

        if (!colorThematic || !layerView.styleSettings.colorThematicSettings?.enabled || colorThematic.$type === "NoneColorThematic") {
            return defaultColor;
        }
        else if (colorThematic.$type === "DistinctColorThematic") {
            const valEx = this.getAttrValue(colorThematic);
            const colorExp = [];
            colorExp.push('case')
            const ranges = colorThematic.ranges;
            const isAttributeBoolean = ranges.filter(r => ["True", "False"].includes(r.val)).length > 0
            ranges.forEach((r: any) => {
                const val = isAttributeBoolean ? String(r.val.toLowerCase()) : r.val;
                colorExp.push(["==", ['to-string', valEx], val]); // Condition
                colorExp.push(getColor(r.color)); // Value
            });
            colorExp.push(defaultColor);
            return colorExp as DataDrivenPropertyValueSpecification<T>;
        }
        else if (colorThematic.$type === "RangeColorThematic") {
            const valEx = this.getAttrValue(colorThematic);
            const colorExp = [];
            colorExp.push('case')
            const ranges = colorThematic.ranges;
            ranges.forEach((r: any, i: number) => {
                const op = i < ranges.length - 1 ? '<=' : '>';
                colorExp.push([op, valEx, Number(r.val)]); // Condition
                colorExp.push(getColor(r.color)); // Value
            });
            colorExp.push(defaultColor);
            return colorExp as DataDrivenPropertyValueSpecification<T>;
        }
        else {
            return defaultColor;
        }
    }

    public getServerExpression(layerView: ILayerView, colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<number> {
        if (layerView.styleSettings.colorPerFeatureSettings?.enabled) {
            return ['get', 'ES_STYLE_COLOR'];
        }
        return this.getColorExpression(layerView, true, true, colorThematic);
    }

    public getHexExpression(layerView: ILayerView, colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<string> {
        if (layerView.styleSettings.colorPerFeatureSettings?.enabled) {
            return DefaultColorThematicStyler.serverToHex(['get', 'ES_STYLE_COLOR']);
        }
        return this.getColorExpression(layerView, false, false, colorThematic);
    }

    public getFilterExpression(layerView: ILayerView, colorThematic?: ColorThematic): ExpressionFilterSpecification | undefined {
        if (!colorThematic || !layerView.styleSettings.colorThematicSettings?.enabled) {
            return undefined;
        }
        const rangesToHide = colorThematic.ranges.filter(t => t.hideFeatures).flatMap(t => t.val === '' ? ['', null] : t.val);
        if (rangesToHide.length === 0) {
            // all color ranges are being shown. No need to create filter;
            return undefined;
        }

        if (colorThematic.$type === "DistinctColorThematic") {
            return ['!', ['in', ['get', colorThematic.attribute], ['literal', rangesToHide]]];
        }

        if (colorThematic.$type === "RangeColorThematic") {

            const attribute = colorThematic.attribute;
            const rangesFilters: ExpressionFilterSpecification[] = [];

            if(colorThematic.ranges[0].hideFeatures) {
                rangesFilters.push(['<=', ['get', attribute], +colorThematic.ranges[0].val]);
            }

            for(let i = 1; i < colorThematic.ranges.length - 1; i++){
                const range = colorThematic.ranges[i];
                const prevRange = colorThematic.ranges[i-1];
                if(!range.hideFeatures) {
                    continue;
                }
                rangesFilters.push(['all', ['<=', ['get', attribute], +range.val], ['>', ['get', attribute], +prevRange.val]]);
            }

            const lastRange = colorThematic.ranges[colorThematic.ranges.length -1];
            if(lastRange.hideFeatures) {
                rangesFilters.push(['>', ['get', attribute], +lastRange.val])
            }

            return ['!', ['any', ...rangesFilters]];
        }
    }

    public getOpacity(layerView: ILayerView): DataDrivenPropertyValueSpecification<number> {
        const transparency = es.util.color.getTransparency(es.api.style.getMainColor(layerView.styleSettings.defaultStyle));
        return 1 - (transparency / 100);
    }

    public getFillHex(layerView: ILayerView, colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<string> {
        const defaultFillColor: number =  layerView.styleSettings.defaultStyle.fillColor;
        if (layerView.styleSettings.colorPerFeatureSettings?.enabled) {
            return DefaultColorThematicStyler.serverToHex(['get', 'ES_STYLE_COLOR']);
        }
        return this.getColorExpression(layerView, false, false, colorThematic, defaultFillColor);
    }

    public getFillOpacity(layerView: ILayerView): DataDrivenPropertyValueSpecification<number> {
        if (layerView.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle") {
            return 0.5;
        }
        if (layerView.styleSettings.defaultStyle.$type === "PolygonStyle" && !layerView.styleSettings.defaultStyle.fillColor) {
            return 0;
        }
        return this.getOpacity(layerView);
    }

    public getStrokeOpacity(layerView: ILayerView): DataDrivenPropertyValueSpecification<number> {
        if (layerView.styleSettings.$type === 'PolygonStyleSettings') {
            return 1;
        }
        return this.getOpacity(layerView);
    }

    public getStrokeHex(layerView: ILayerView, colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<string> {
        const defaultLineColor = layerView.styleSettings.defaultStyle?.line?.color || layerView.styleSettings.defaultStyle?.polygonStyle?.line?.color;

        // All polygon/line datasets use default line color not the thematic color
        // The only exception is for DriveTime/Radius buffer analysis

        if (layerView.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle" && layerView.styleSettings.colorThematicSettings?.enabled) {
            return this.getColorExpression(layerView, false, false, colorThematic, defaultLineColor);
        }

        return es.util.color.serverToHex(defaultLineColor);
    }

    /**
     * Build an Azure Expression that convert a server color to a hexadecimal color (eg. #FF00FF)
     * @param {number|Expression} serverColor The server color input, can be a number or an expression
     */
    public static serverToHex(serverColor:  DataDrivenPropertyValueSpecification<number>) {
        function decToHex(input: any) {
            return [
                'match',
                input,
                10, 'A',
                11, 'B',
                12, 'C',
                13, 'D',
                14, 'E',
                15, 'F',
                ['to-string', input]
            ]
        }

        const exp: any = ['concat', '#'];
        for (let i = 5; i >= 0; i--){
            exp.push(decToHex(['%', ['floor', ['/', serverColor, ['^', 16, i]]], 16]));
        }
        return exp;
    }

    public static async getColorPerFeatureColors(layerViewId: number): Promise<number[]> {
        return new Promise((resolve, reject) => {
            es.api.feature.listLean(layerViewId, ["ES_STYLE_COLOR"]).then(features => {
                const colors = features.map(f => f.attrs["ES_STYLE_COLOR"] as number);
                resolve(colors);
            }).catch(e => reject(e));
        });
    }
}

export class TerritoryColorThematicStyler implements ColorThematicStyler {

    private readonly _territoryModel: TerritoryModel;
    private _selection: boolean = false;

    private baseFeaturesColor: {[bfId: number]: string} = {};
    private baseFeaturesStrokeOpacity: {[bfId: number]: number} = {};

    constructor(territoryModel: TerritoryModel) {
        this._territoryModel = territoryModel;

        this.update();
    }

    public async getThematic(layerView: ILayerView): Promise<ColorThematic|undefined> {
        return new DefaultColorThematicStyler().getThematic(layerView);
    }

    public update() {
        if (this._territoryModel.isAccountOnly()) {
            return;
        }

        const currentLevel = this._territoryModel.getCurrentLevel() ?? 1;
        const filteredInTerritoryIds = this._territoryModel.getFilter()||[];

        // Single array for base feature fill color; index = base feature id
        this.baseFeaturesColor = {}

        this.baseFeaturesStrokeOpacity = {};

        this._territoryModel.getTerritoryFeaturesObjRef().forEach(t => {
            const isFilteredOut = filteredInTerritoryIds.length > 0 && !filteredInTerritoryIds.includes(t.ES_ID);

            let color = t.ES_STYLE_COLOR;
            const terLevel = t.ES_LEVEL ?? 1;
            if (currentLevel < terLevel) {
                const parentId = this._territoryModel.getAncestors(t.ES_ID, "ES_ID", currentLevel)[0];
                if (parentId != null) {
                    const parent = this._territoryModel.get(parentId);
                    if (parent) {
                        color = parent.ES_STYLE_COLOR ?? color;
                    }
                }
            }
            t.ES_BASE_FEATURE_IDS.forEach( (bfId: number) => {
                this.baseFeaturesStrokeOpacity[bfId] = 0; // Assigned and filter-out base feats have no borders
                if (!isFilteredOut) {
                    // Color for assigned and filtered in base feat
                    this.baseFeaturesColor[bfId] = es.util.color.serverToHex(color);
                }
            });
        });
    }

    private getBaseFeatureColor<T>(): DataDrivenPropertyValueSpecification<T> {
        return ['to-color', ["get", ['to-string', ["id"]], ['literal', this.baseFeaturesColor]]];
    }

    private getExtraPointFeatureColor(layerView: ILayerView) {
        if (!this._territoryModel?.territoryFeatures?.length){
            return es.util.color.simplifyServer(layerView.styleSettings.defaultStyle.color);
        }
        let colorExp: any = ['case'];

        let level = this._territoryModel.getCurrentLevel();

        const criteria = this._territoryModel.getManageTerritoryMainLayerView().getCriteriaObjRef();
        let inValues:Array<number>|null;
        if (criteria && criteria.$type == 'In' && criteria.attr == 'ES_ID') {
            inValues = criteria.values;
        }

        this._territoryModel.territoryFeatures.forEach((tf: ITerritoryFeature) => {
            if ((!this._territoryModel.isHierarchical() || tf.ES_LEVEL === level) && (!inValues || inValues.includes(tf.ES_ID))) {
                let color = es.util.color.simplifyServer(tf.ES_STYLE_COLOR);
                let basePointFeatIds = this._territoryModel.getBasePointFeatureIds(tf.ES_ID);
                colorExp.push(['in', ['id'], ['literal', basePointFeatIds]]);
                colorExp.push(color);
            }
        });
        colorExp.push(es.util.color.simplify(es.util.color.serverToHex(layerView.styleSettings.defaultStyle.color)));
        return colorExp;
    }

    private getExtraPointFeatureOpacity() : DataDrivenPropertyValueSpecification<number> {
        if (!this._territoryModel?.territoryFeatures?.length){
            return 1;
        }
        let colorExp: any = ['case'];

        let level = this._territoryModel.getCurrentLevel();

        const criteria = this._territoryModel.getManageTerritoryMainLayerView().getCriteriaObjRef();
        let inValues:Array<number>|null;
        if (criteria && criteria.$type == 'In' && criteria.attr == 'ES_ID') {
            inValues = criteria.values;
        }

        const basePointFeatIds: number[] = [];

        this._territoryModel.territoryFeatures.forEach((tf: ITerritoryFeature) => {
            if ((!this._territoryModel.isHierarchical() || tf.ES_LEVEL === level) && (!inValues || inValues.includes(tf.ES_ID))) {
                basePointFeatIds.push(...this._territoryModel.getBasePointFeatureIds(tf.ES_ID));
            }
        });

        colorExp.push(['in', ['id'], ['literal', basePointFeatIds]]);
        colorExp.push(1);
        colorExp.push(0);

        return [
            'step',
            ["zoom"],
            colorExp,
            NavUtils.resolutionToZoom(this._territoryModel.getBaseLayerConfigClone().maxRes),
            1];
    }

    private getBaseFeatureOpacity(): DataDrivenPropertyValueSpecification<number> {
        return ['case', ["has", ['to-string', ["id"]], ['literal', this.baseFeaturesColor]], 0.5, 0];
    }

    private getBaseFeatureStrokeOpacity(): DataDrivenPropertyValueSpecification<number> {
            return ['coalesce', ["get", ['to-string', ["id"]], ['literal', this.baseFeaturesStrokeOpacity]], 1];
    }

    private getColorExpression<T extends number | string>(layerView: ILayerView,
                                                          isServer: boolean,
                                                          simplify: boolean,
                                                          colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<T> {

        if (layerView.$type === "ManageTerritoryBaseLayerView") {
            return this.getBaseFeatureColor();
        }

        if (this._territoryModel.isRelatedExtraPointLayer(layerView.id)) {
            return this.getExtraPointFeatureColor(layerView);
        }

        if (!(layerView.styleSettings as TerritoryPointStyleSettings).useTerritoryColor) {
            const colorStyler = new DefaultColorThematicStyler();
            return (isServer ? colorStyler.getServerExpression(layerView, colorThematic) :
                colorStyler.getHexExpression(layerView, colorThematic)) as DataDrivenPropertyValueSpecification<T>;
        }

        const getColor = (terId: number): T => {
            let color = layerView.styleSettings.defaultStyle.color;
            if (terId > 0) {
                color = this._territoryModel.get(terId).ES_STYLE_COLOR;
            }

            if (isServer) {
                return (simplify ? es.util.color.simplifyServer(color) : color) as T;
            }
            return es.util.color.serverToHex(color) as T;
        };

        const nodes = this._territoryModel.getPointMapping().layerPointMapping.get(layerView.layerMetaId);
        if (nodes) {
            const map: {[featId: number]: T} = {};

            for (const [e, n] of nodes.entries()) {
                const terId = !this._territoryModel.isLowestLevel() ?
                    this._territoryModel.getAncestors(n.territoryId, undefined, this._territoryModel.getCurrentLevel())[0] :
                    n.territoryId;
                if (terId > 0) {
                    map[e] = getColor(terId);
                }
            }

            const key = ['case',
                ['has', 'point_count'],
                ['to-string', ['get', 'ES_ID']],
                ['to-string', ['id']]
            ] as DataDrivenPropertyValueSpecification<any>;

            return [
                'let', 'map', ['literal', map], 'key', key,
                [
                    'case',
                    ['has', ['var', 'key'], ['var', 'map']],
                    ['get', ['var', 'key'], ['var', 'map']],
                    getColor(-1)
                ]
            ];
        }
        return getColor(-1);
    }

    public getServerExpression(layerView: ILayerView, colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<number> {
        const color = this.getColorExpression(layerView, true, true, colorThematic) as ExpressionSpecification;
        if (this._selection) {
            return ['concat', color, '_sel']
        }
        return color;
    }

    public getHexExpression(layerView: ILayerView, colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<string> {
        let color =  this.getColorExpression(layerView, false, false, colorThematic) as any;
        if (this._selection) {
            return [
                'let', 'color', color,
                ['rgba',
                    ['/', ['at', 0, ['to-rgba', ['var', 'color']]], 2],
                    ['/', ['at', 1, ['to-rgba', ['var', 'color']]], 2],
                    ['/', ['at', 2, ['to-rgba', ['var', 'color']]], 2],
                    1,
                ]
            ];
        }
        return color;
    }

    public getFilterExpression(layerView: ILayerView, colorThematic?: ColorThematic): ExpressionFilterSpecification | undefined {
        return new DefaultColorThematicStyler().getFilterExpression(layerView, colorThematic);
    }

    public getOpacity(layerView: ILayerView): DataDrivenPropertyValueSpecification<number> {
        if (this._territoryModel.isBaseLayer(layerView.id)) {
            return this.getBaseFeatureOpacity();
        }
        if (this._territoryModel.isRelatedLayer(layerView.id)) {
            return 0;
        }
        if (this._territoryModel.isRelatedExtraPointLayer(layerView.id)){
            return this.getExtraPointFeatureOpacity();
        }
        return new DefaultColorThematicStyler().getOpacity(layerView);
    }

    public getFillHex(layerView: ILayerView, colorThematic?: ColorThematic): DataDrivenPropertyValueSpecification<string> {
        return this.getHexExpression(layerView, colorThematic);
    }

    public getFillOpacity(layerView: ILayerView): DataDrivenPropertyValueSpecification<number> {
        if (this._territoryModel.isOutlinesLayer(layerView.id)) {
            return 0;
        }
        return this.getOpacity(layerView);
    }

    public getStrokeOpacity(layerView: ILayerView): DataDrivenPropertyValueSpecification<number> {
        if (layerView.$type === "ManageTerritoryBaseLayerView") {
            return this.getBaseFeatureStrokeOpacity();
        }
        return new DefaultColorThematicStyler().getStrokeOpacity(layerView);
    }

    public getStrokeHex(layerView: ILayerView, colorThematic?: ColorThematic) {
        return new DefaultColorThematicStyler().getStrokeHex(layerView, colorThematic);
    }

    public get selection(): boolean {
        return this._selection;
    }

    public set selection(value: boolean) {
        this._selection = value;
    }
}

export class ScheduleColorThematicStyler extends DefaultColorThematicStyler {

    private readonly schedule: IRoutingModule;


    constructor(schedule: IRoutingModule) {
        super();
        this.schedule = schedule;
    }

    public getStrokeHex(layerView: ILayerView) {
        const shiftId = (layerView as IScheduleLayerView).shiftId;
        return this.schedule.isShiftActive(shiftId) ?
            this.schedule.getShiftColorHex(shiftId) :
            this.schedule.getInactiveColorHex();
    }

    public getFilterExpression(layerView: ILayerView, colorThematic?: ColorThematic): ExpressionFilterSpecification | undefined {

        let filter =  super.getFilterExpression(layerView, colorThematic);

        const stopsFeatureIds = _.uniq(this.schedule.getServicesObjRef()
            .filter((s:any) => s.layerViewId === layerView.id)
            .map((s:any) => s.featureId)).sort() as number[];

        // 1. No stop/services, do nothing
        if (stopsFeatureIds.length == 0) {
            return filter;
        }

        // 2. Feature is a stop/service,  We use a filter for not-clustered features, they will be omitted for labeling clash detection.
        const stopFilter =  ['!', ["in", ['id'], ['literal', stopsFeatureIds]]] as ExpressionFilterSpecification;
        if (filter) {
            return ['all', filter, stopFilter]
        }
        return stopFilter;
    }
}